import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Segment, Header, Icon, Message, Button, Popup } from "semantic-ui-react";
import { DataTable, getCommonAPI } from "hpp-fe-common";

import { setActiveSideNavID } from "../../loyaltySideNavSlice";
import {
  claimedHistoryInprogress,
  claimedHistoryProcessed,
} from "./claimedHistorySlice";
import { signOut } from "../../../auth/authSlice";
import ClaimedHistoryModal from "./ClaimedHistoryModal";

const API_PATH = "/api_v1/points/my-claimed-history";

const columns = [
  { name: "Redeemed Company", selector: (row) => row.upstreamCompanyName, sortable: true, grow: 2 },
  {
    name: "Redeemed Time",
    selector: (row) => row.redeemAt,
    sortable: true,
    grow: 2,
    cell: (row) => (row.redeemAt ? new Date(row.redeemAt).toLocaleString() : "-"),
  },
  { name: "Serial Count", selector: (row) => row.serialCount, sortable: true },
  { name: "Total Points", selector: (row) => row.totalRedeemPoints, sortable: true },
];

const filterTypes = [
  { key: "upstreamCompanyName", text: "By Redeemed Company", value: "upstreamCompanyName" }
];

function ClaimedHistory() {
  const dispatch = useDispatch();

  const { loading, error, errorMessage, errorMessageData, items } = useSelector(
    (state) => state.claimedHistory
  );

  const [selectedRows, setSelectedRows] = useState([]);
  const [toggledClearRows, setToggleClearRows] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    dispatch(setActiveSideNavID("claimedHistory"));
  }, [dispatch]);

  const refresh = useCallback(() => {
    dispatch(claimedHistoryInprogress());

    const requestURL = encodeURI(`${API_PATH}?portal=inst`);

    getCommonAPI()
      .get(requestURL)
      .then(({ data }) => {
        if (data?.authenticated === false) {
          return dispatch(
            signOut({
              error: true,
              errorMessage: data.errorMessage,
              errorMessageData: data.errorMessageData,
            })
          );
        }

        if (data?.error) {
          dispatch(
            claimedHistoryProcessed({
              error: true,
              errorMessage: data?.errorMessage || "Error loading data",
              errorMessageData: data?.errorMessageData || "",
              items: [],
            })
          );
        } else {
          dispatch(
            claimedHistoryProcessed({
              items: data?.data?.items || [],
            })
          );
        }
      })
      .catch((err) => {
        dispatch(
          claimedHistoryProcessed({
            error: true,
            errorMessage: "Error loading claimed history. Please contact system administrator",
            errorMessageData: err.toString(),
            items: [],
          })
        );
      });
  }, [dispatch]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleChange = ({ selectedRows }) => setSelectedRows(selectedRows);

  const handleClearRows = () => {
    setSelectedRows([]);
    setToggleClearRows(!toggledClearRows);
  };

  const refreshViaButton = () => {
    handleClearRows();
    refresh();
  };

  const handleViewDetails = () => {
    if (selectedRows.length === 1) {
      setModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const showEmpty = !loading && !error && (!Array.isArray(items) || items.length === 0);

  return (
    <Segment loading={loading}>
      <Header as="h3">
        <Icon name="history" />
        <Header.Content>My Claimed History</Header.Content>
      </Header>

      <Popup
        position="left center"
        content="Refresh data"
        trigger={
          <Button
            color="black"
            onClick={refreshViaButton}
            size="massive"
            style={{ position: "fixed", right: "10px", bottom: "30px", zIndex: 100 }}
            circular
            icon="sync alternate"
          />
        }
      />

      {error && (
        <Message negative>
          <Message.Header>Error loading Claimed History</Message.Header>
          <p>{typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage)}</p>
          {errorMessageData && <p>{typeof errorMessageData === 'string' ? errorMessageData : JSON.stringify(errorMessageData)}</p>}
        </Message>
      )}

      {Array.isArray(selectedRows) && selectedRows.length > 0 && (
        <Button onClick={handleClearRows}>
          <Icon name="close" /> Clear Selection
        </Button>
      )}

      {Array.isArray(selectedRows) && selectedRows.length === 1 && (
        <Button color="black" onClick={handleViewDetails}>
          <Icon name="eye" /> View Details
        </Button>
      )}

      {showEmpty && (
        <Message info>
          <Message.Header>No Records</Message.Header>
          <p>No claimed history records found.</p>
        </Message>
      )}

      <DataTable
        columns={columns}
        data={Array.isArray(items) ? items : []}
        filterFromServer={false}
        filterCaption="Search..."
        filterTypes={filterTypes}
        showTotalFor="totalRedeemPoints"
        additionalProps={{
          selectableRows: true,
          selectableRowsSingle: true,
          onSelectedRowsChange: handleChange,
          clearSelectedRows: toggledClearRows,
        }}
      />

      <ClaimedHistoryModal
        open={modalOpen}
        onClose={handleModalClose}
        selectedRow={selectedRows.length === 1 ? selectedRows[0] : null}
      />
    </Segment>
  );
}

export default ClaimedHistory;
